import React, { useState } from "react";
import { KeyRound, MessageCircle, Mail, Copy, Check } from "lucide-react";

// Tarjeta de "aquí está la contraseña recién generada, guárdala ahora" —
// compartida entre alta de viajero, alta de admin y alta de agencia. La
// contraseña real solo existe en la respuesta de creación (el backend nunca
// la vuelve a mandar), así que esta tarjeta es la única oportunidad de
// verla o compartirla.
export default function GeneratedCredential({
  heading,
  loginUrl,
  bookingId,
  bookingIdLabel = "Booking ID",
  secondaryLabel,
  secondaryValue,
  password,
  phone,
  email,
  greetingName,
  productName,
  testId = "generated-credential",
}) {
  const [copied, setCopied] = useState(false);
  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard puede fallar sin HTTPS/permiso — no rompe el flujo, la
      // persona igual ve los datos en pantalla para copiarlos a mano.
    }
  };
  const text =
    `¡Hola ${greetingName}! 👋\n\n` +
    `Ya está listo tu acceso a ${productName || "Travel Shield"}.\n\n` +
    `🔑 Tus datos de acceso:\n` +
    `Enlace: ${loginUrl}\n` +
    `${bookingIdLabel}: ${bookingId}\n` +
    (secondaryLabel ? `${secondaryLabel}: ${secondaryValue}\n` : "") +
    `Contraseña: ${password}\n\n` +
    `¡Nos vemos pronto!`;
  const waLink = phone
    ? `https://wa.me/${phone.replace(/\D/g, "")}?text=${encodeURIComponent(text)}`
    : null;
  const mailtoLink = email
    ? `mailto:${encodeURIComponent(email)}?subject=${encodeURIComponent(`Tu acceso a ${productName || "Travel Shield"}`)}&body=${encodeURIComponent(text)}`
    : null;

  return (
    <div className="mt-6 p-5 rounded-2xl bg-black/40 border border-[var(--brand-cyan,#FF85AC)]/30" data-testid={testId}>
      <p className="text-[10px] uppercase tracking-[0.3em] text-[var(--brand-cyan,#FF85AC)] mb-3">
        <KeyRound className="inline w-3.5 h-3.5 mr-1" />
        {heading}
      </p>
      <p className="text-sm text-neutral-300 mb-1">{bookingIdLabel}: <span className="text-white font-mono">{bookingId}</span></p>
      {secondaryLabel && (
        <p className="text-sm text-neutral-300 mb-1">{secondaryLabel}: <span className="text-white font-mono">{secondaryValue}</span></p>
      )}
      <p className="text-sm text-neutral-300 mb-4">Contraseña: <span className="text-white font-mono">{password}</span></p>
      <p className="text-xs text-neutral-500 mb-4">
        Guárdala ahora — no se puede volver a mostrar. Si se pierde, se puede regenerar desde aquí.
      </p>
      <div className="flex flex-wrap gap-3">
        <button
          type="button"
          onClick={copyAll}
          data-testid={`${testId}-copy`}
          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] px-4 py-2.5 rounded-full font-semibold border border-white/15 text-neutral-200 hover:bg-white/10"
        >
          {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
          {copied ? "Copiado" : "Copiar datos"}
        </button>
        <a
          href={waLink || "#"}
          target="_blank"
          rel="noreferrer"
          aria-disabled={!waLink}
          data-testid={`${testId}-whatsapp`}
          className={`inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] px-4 py-2.5 rounded-full font-semibold ${
            waLink ? "bg-[#25D366] text-black hover:brightness-95" : "bg-white/5 text-neutral-600 cursor-not-allowed pointer-events-none"
          }`}
        >
          <MessageCircle className="w-3.5 h-3.5" /> Enviar por WhatsApp
        </a>
        <a
          href={mailtoLink || "#"}
          aria-disabled={!mailtoLink}
          data-testid={`${testId}-email`}
          className={`inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] px-4 py-2.5 rounded-full font-semibold border ${
            mailtoLink
              ? "border-[var(--brand-cyan,#FF85AC)]/40 text-[var(--brand-cyan,#FF85AC)] hover:bg-[var(--brand-cyan,#FF85AC)]/10"
              : "border-white/10 text-neutral-600 cursor-not-allowed pointer-events-none"
          }`}
        >
          <Mail className="w-3.5 h-3.5" /> Enviar por correo
        </a>
      </div>
      {!waLink && !mailtoLink && (
        <p className="text-xs text-[#FF3B30] mt-3">
          No hay correo ni teléfono capturados — copia las credenciales de arriba y compártelas manualmente.
        </p>
      )}
    </div>
  );
}
